import { Dispatch } from 'react';
import firebase from './firebase';
import { AuthAction } from './types';
import {
  AUTH_LOADING,
  AUTH_SUCCESS,
  AUTH_ERROR,
  AUTH_LOGOUT,
  AUTH_TOKEN,
  AUTH_TOKEN_KEY,
} from './constants';
import { chackAvailability } from 'graphql/utils';
import { DOES_ACCOUNT_EXIST } from 'graphql/account';

const getErrorMessage = (code: string) => {
  switch (code) {
    case 'auth/expired-action-code':
    case 'auth/invalid-action-code':
      return 'the link has expired';
    case 'auth/user-not-found':
      return 'no user with that email exists';
    case 'auth/user-disabled':
      return 'the user is disabled';
    default:
      return "something's gone wrong";
  }
};

export const signUp = async (dispatch: Dispatch<AuthAction>, email: string, password: string) => {
  dispatch({ type: AUTH_LOADING, isLoading: true });
  try {
    await firebase.auth().createUserWithEmailAndPassword(email, password);
  } catch (e) {
    console.log(`[ERROR] signing up new user ${e.message}`);
    dispatch({ type: AUTH_ERROR, message: e.message });
  }
};

export const signIn = async (dispatch: Dispatch<AuthAction>, email: string, password: string) => {
  dispatch({ type: AUTH_LOADING, isLoading: true });
  try {
    await firebase.auth().signInWithEmailAndPassword(email, password);
  } catch (e) {
    console.log(`[ERROR] signing in user ${e.message}`);
    dispatch({ type: AUTH_ERROR, message: e.message });
  }
};

export const signOut = (dispatch: Dispatch<AuthAction>) => {
  firebase
    .auth()
    .signOut()
    .catch((e) => {
      console.log(`[ERROR] signing out user ${e.message}`);
      dispatch({ type: AUTH_LOGOUT });
    });
};

export const sendPasswordReset = async (dispatch: Dispatch<AuthAction>, email: string) => {
  try {
    await firebase.auth().sendPasswordResetEmail(email);
  } catch (e) {
    console.log(`[ERROR] sending user's password reset email ${e.message}`);
    dispatch({ type: AUTH_ERROR, message: e.message });
  }
};

export const updateEmail = async (dispatch: Dispatch<AuthAction>, email: string) => {
  dispatch({ type: AUTH_LOADING, isLoading: true });
  try {
    await firebase.auth().currentUser?.updateEmail(email);
  } catch (e) {
    console.log(`[ERROR] updating user's email ${e.message}`);
    dispatch({ type: AUTH_ERROR, message: e.message });
  } finally {
    dispatch({ type: AUTH_LOADING, isLoading: false });
  }
};

export const resetPassword = async (
  dispatch: Dispatch<AuthAction>,
  code: string,
  password: string,
) => {
  dispatch({ type: AUTH_LOADING, isLoading: true });
  try {
    const email = await firebase.auth().verifyPasswordResetCode(code);
    await firebase.auth().confirmPasswordReset(code, password);
    await firebase.auth().signInWithEmailAndPassword(email, password);
  } catch (e) {
    console.log(`[ERROR] resetting user's password ${e.code}: ${e.message}`);
    dispatch({ type: AUTH_ERROR, message: getErrorMessage(e.code) });
  } finally {
    dispatch({ type: AUTH_LOADING, isLoading: false });
  }
};

export const stopLoading = (dispatch: Dispatch<AuthAction>) => {
  dispatch({ type: AUTH_LOADING, isLoading: false });
};
export const setToken = (dispatch: Dispatch<AuthAction>, token: string) => {
  const shouldContineuLoading = token !== '';
  dispatch({ type: AUTH_TOKEN, token, isLoading: shouldContineuLoading });
};

export const onAuthStateChanged = (dispatch: Dispatch<AuthAction>) => {
  firebase.auth().onAuthStateChanged(async (firebaseUser) => {
    dispatch({ type: AUTH_LOADING, isLoading: true });
    if (firebaseUser) {
      const { uid: userId, email } = firebaseUser;
      const token = await firebaseUser.getIdToken();
      localStorage.setItem(AUTH_TOKEN_KEY, token);

      const { doesAccountExist } = await chackAvailability(DOES_ACCOUNT_EXIST, undefined, token);
      dispatch({
        type: AUTH_SUCCESS,
        userId,
        email: email || '',
        token,
        doesAccountExist,
      });
    } else {
      dispatch({ type: AUTH_LOGOUT });
      localStorage.setItem(AUTH_TOKEN_KEY, '');
    }
  });
};
